import type { GridColDef } from '@mui/x-data-grid';
import { EmptyTableCell } from '@/components/EmptyTableCell';
import { formatAmount, formatDecimal, getPercentage } from '../../helpers';
import type {
  TeamPerformanceAgentData,
  TeamPerformanceTeamData,
} from '../hooks/useTeamPerformance';
import { PercentageCellWithBackground } from './PercentageCellWithBackground';
import { TeamCell, UserCell } from './TeamPerformanceTable.helpers';

type GridColumn = GridColDef<
  TeamPerformanceAgentData | TeamPerformanceTeamData
>;

const TransactionsColumn: GridColumn = {
  field: 'sides',
  headerName: 'Transactions',
  flex: 0.5,
  minWidth: 100,
};

const TotalTransactionsColumn: GridColumn = {
  field: 'sides',
  headerName: 'Total Transactions',
  flex: 0.5,
  minWidth: 100,
};

const SalesVolumeColumn: GridColumn = {
  field: 'salesVolume',
  headerName: 'Sales Volume',
  flex: 1,
  valueFormatter: formatAmount,
  minWidth: 120,
};

const AverageSalesValueColumn: GridColumn = {
  field: 'averageSalesValue',
  headerName: 'Avg Sale Value',
  valueFormatter: formatAmount,
  flex: 1,
  minWidth: 140,
};

const WeightedSalesVolumeColumn: GridColumn = {
  field: 'weightedSalesVolume',
  headerName: 'Weighted Sales Volume',
  flex: 1,
  minWidth: 170,
  valueFormatter: formatAmount,
};

const WeightedGCIColumn: GridColumn = {
  field: 'weightedAdjustedGrossCommission',
  headerName: 'Weighted GCI',
  renderCell: ({ row }) => formatAmount(row.weightedAdjustedGrossCommission),
  flex: 1,
  minWidth: 120,
  valueFormatter: (_, row) => formatAmount(row.weightedAdjustedGrossCommission),
};

const WeightedTransactionsColumn: GridColumn = {
  field: 'weightedSides',
  headerName: 'Weighted Transactions',
  flex: 0.5,
  minWidth: 160,
  valueFormatter: (value: number | null) => formatDecimal(value),
};

export const AdjustedGCIColumn: GridColumn = {
  field: 'adjustedGrossCommission',
  headerName: 'Post-Referral GCI',
  renderCell: ({ row }) => formatAmount(row.adjustedGrossCommission),
  flex: 1,
  minWidth: 120,
  valueFormatter: (_, row) => formatAmount(row.adjustedGrossCommission),
};

const AverageCommissionColumn: GridColumn = {
  field: 'averageCommissionAmount',
  headerName: 'Average Commission',
  flex: 1,
  minWidth: 170,
  valueFormatter: formatAmount,
  renderCell: ({ row }) =>
    row.averageCommissionAmount != null ? (
      formatAmount(row.averageCommissionAmount)
    ) : (
      <EmptyTableCell />
    ),
};

const WeightedCommissionColumn: GridColumn = {
  field: 'weightedAverageCommissionAmount',
  headerName: 'Average Commission',
  flex: 1,
  minWidth: 170,
  valueFormatter: formatAmount,
  renderCell: ({ row }) =>
    row.weightedAverageCommissionAmount != null ? (
      formatAmount(row.weightedAverageCommissionAmount)
    ) : (
      <EmptyTableCell />
    ),
};

const SalesVolumeGoalColumn: GridColumn = {
  field: 'salesValueGoal',
  headerName: 'Sales Volume Goal',
  flex: 1,
  minWidth: 120,
  valueFormatter: (value: number | null) =>
    value != null ? formatAmount(value) : '',
  renderCell: ({ row }) =>
    row.salesValueGoal != null ? (
      formatAmount(row.salesValueGoal)
    ) : (
      <EmptyTableCell />
    ),
};

const AvgSaleValueGoalColumn: GridColumn = {
  field: 'averageSaleValueGoal',
  headerName: 'Avg Sale Value Goal',
  flex: 1,
  minWidth: 140,
  valueFormatter: (value: number | null) =>
    value != null ? formatAmount(value) : '',
  renderCell: ({ row }) =>
    row.averageSaleValueGoal != null ? (
      formatAmount(row.averageSaleValueGoal)
    ) : (
      <EmptyTableCell />
    ),
};

const TransactionCountGoalColumn: GridColumn = {
  field: 'sidesGoal',
  headerName: 'Transaction Count Goal',
  flex: 0.5,
  minWidth: 100,
  valueFormatter: (value: number | null) =>
    value != null ? String(Math.round(value)) : '',
  renderCell: ({ row }) =>
    row.sidesGoal != null ? Math.round(row.sidesGoal) : <EmptyTableCell />,
};

const PercentToSalesVolumeGoalColumn: GridColumn = {
  field: 'percentToSalesVolumeGoal',
  headerName: '% to Sales Volume Goal',
  flex: 1,
  minWidth: 190,
  renderCell: ({ row }) => (
    <PercentageCellWithBackground
      total={row.salesValueGoal}
      currentValue={row.salesVolume}
      backgroundColorFilled="secondary.500"
      backgroundColorEmpty="secondary.300"
      showTotal
    />
  ),
  valueFormatter: (_, row) =>
    getPercentage(row.salesVolume, row.salesValueGoal),
};

const AgentColumn: GridColumn = {
  field: 'agent',
  headerName: 'Agent',
  flex: 1,
  minWidth: 150,
  renderCell: ({ row }) =>
    'agent' in row ? (
      <UserCell displayName={row.agent} photoUrl={row.agentPhotoUrl} />
    ) : null,
};

const TeamColumn: GridColumn = {
  field: 'team',
  headerName: 'Team',
  minWidth: 150,
  flex: 1,
  renderCell: ({ row }) =>
    'team' in row ? <TeamCell teamName={row.team} /> : null,
};

export const AgentColumns: GridColumn[] = [
  AgentColumn,
  TotalTransactionsColumn,
  SalesVolumeColumn,
  AverageSalesValueColumn,
  WeightedSalesVolumeColumn,
  WeightedGCIColumn,
  WeightedTransactionsColumn,
  WeightedCommissionColumn,
  SalesVolumeGoalColumn,
  AvgSaleValueGoalColumn,
  TransactionCountGoalColumn,
  PercentToSalesVolumeGoalColumn,
];

export const TeamColumns: GridColumn[] = [
  TeamColumn,
  TransactionsColumn,
  SalesVolumeColumn,
  AverageSalesValueColumn,
  WeightedSalesVolumeColumn,
  WeightedGCIColumn,
  WeightedTransactionsColumn,
  AverageCommissionColumn,
  SalesVolumeGoalColumn,
  AvgSaleValueGoalColumn,
  TransactionCountGoalColumn,
  PercentToSalesVolumeGoalColumn,
];

const GOAL_COLUMN_FIELDS: readonly string[] = [
  'salesValueGoal',
  'averageSaleValueGoal',
  'sidesGoal',
  'percentToSalesVolumeGoal',
];

/**
 * The returned array backs the grid, the column-options panel, and the CSV
 * export alike, so dropping a column here drops it from all three.
 *
 * `hidePercentToGoal` gates only the % column, matching the pre-existing
 * per-period behavior; `goalsAvailable: false` removes goal UI entirely.
 */
export function filterGoalColumns(
  columns: GridColumn[],
  {
    goalsAvailable,
    hidePercentToGoal,
  }: { goalsAvailable: boolean; hidePercentToGoal: boolean },
): GridColumn[] {
  if (!goalsAvailable) {
    return columns.filter((col) => !GOAL_COLUMN_FIELDS.includes(col.field));
  }
  if (hidePercentToGoal) {
    return columns.filter((col) => col.field !== 'percentToSalesVolumeGoal');
  }
  return columns;
}

export const HIDDEN_BY_DEFAULT_COLUMN_FIELDS = [
  'salesValueGoal',
  'averageSaleValueGoal',
  'sidesGoal',
  'weightedSalesVolume',
  'weightedSides',
] as const;
